import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import trackingService, {
  type TraceResult,
  type TraceCarrierType,
} from '../../services/tracking.service';
import { useToast } from '../../contexts/ToastContext';
import TraceResultModal from './TraceResultModal';

/**
 * Bulk "Trace All" for the PROs currently in view — a dropdown with the two
 * v1 options, "on SSLine" (scac) and "on Terminal" (rail). The host supplies a
 * getPronumbers() callback (all rows in a search result, or one dashboard
 * card's rows). We de-dupe, cap, POST /tracking/trace-all with the chosen
 * tracetype, and show the per-PRO results panel (which polls for legacy
 * progress).
 */
interface Props {
  getPronumbers: () => number[];
  label?: string;
  className?: string;
  disabled?: boolean;
  /** 'text' (default) = "Trace All ▾" pill; 'icon' = compact toolbar icon that
   *  opens the same SSLine/Terminal menu (for a table's icon cluster). */
  variant?: 'text' | 'icon';
  /** Leading-icon size for the text variant (default 'h-4 w-4'). Pass a smaller
   *  value (e.g. 'h-3.5 w-3.5') where the sibling buttons are text-xs. */
  iconClassName?: string;
}

const MAX = 500;

// Colored pill matching the header/action-bar mass-action buttons (Change All =
// amber, Change Template = blue); teal reads as the "tracking/location" action.
const DEFAULT_CLS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50';

const OPTIONS: { tracetype: TraceCarrierType; label: string }[] = [
  { tracetype: 'scac', label: 'Trace All on SSLine' },
  { tracetype: 'rail', label: 'Trace All on Terminal' },
];

// Location-with-tracking-rings glyph (a map pin over concentric radar rings) —
// the "trace" icon for the toolbar. Uses currentColor so it inherits text-white.
const TraceLocationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 2.5c-2.76 0-5 2.24-5 5 0 3.2 3.5 6.4 5 7.7 1.5-1.3 5-4.5 5-7.7 0-2.76-2.24-5-5-5z" />
    <circle cx="12" cy="7.3" r="1.9" />
    <ellipse cx="12" cy="19.4" rx="3" ry="1.2" />
    <ellipse cx="12" cy="19.4" rx="6.2" ry="2.6" />
  </svg>
);

const TraceAllButton: React.FC<Props> = ({ getPronumbers, label = 'Trace All', className, disabled, variant = 'text', iconClassName }) => {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<TraceResult[] | null>(null);
  const [title, setTitle] = useState(label);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // Open the menu in a portal at fixed coords so it escapes the toolbar's
  // stacking context / sticky table header (which otherwise paints over it).
  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next && triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        // Right-align under the trigger (menu is shifted left by its own width).
        setMenuPos({ top: r.bottom + 4, left: r.right });
      }
      return next;
    });
  };

  const run = async (tracetype: TraceCarrierType, optLabel: string) => {
    setOpen(false);
    const pros = Array.from(new Set(getPronumbers())).filter((n) => Number.isFinite(n));
    if (pros.length === 0) {
      showToast({ type: 'info', message: 'No PROs in view to trace' });
      return;
    }
    if (pros.length > MAX) {
      showToast({ type: 'error', message: `Too many PROs (${pros.length}); max ${MAX} per Trace All` });
      return;
    }
    setTitle(`${optLabel} (${pros.length})`);
    setLoading(true);
    try {
      const resp = await trackingService.traceAll(pros, tracetype);
      setResults(resp.results);
    } catch (e: any) {
      showToast({ type: 'error', message: e?.response?.data?.detail || 'Trace All failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={triggerRef} className="relative inline-block">
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={toggle}
          disabled={disabled || loading}
          aria-haspopup="menu"
          aria-expanded={open}
          title="Trace All (SSLine / Terminal)"
          aria-label="Trace All"
          className={
            className ??
            'p-1 rounded transition-colors text-white hover:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50'
          }
        >
          <TraceLocationIcon className={`h-4 w-4 ${loading ? 'animate-pulse' : ''}`} />
        </button>
      ) : (
        <button
          type="button"
          onClick={toggle}
          disabled={disabled || loading}
          aria-haspopup="menu"
          aria-expanded={open}
          className={className ?? DEFAULT_CLS}
        >
          <TraceLocationIcon className={`${iconClassName ?? 'h-4 w-4'} ${loading ? 'animate-pulse' : ''}`} />
          {loading ? 'Tracing…' : label}
          <span aria-hidden className="text-[0.625rem]">▾</span>
        </button>
      )}

      {open &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
            <div
              role="menu"
              style={{ top: menuPos.top, left: menuPos.left, transform: 'translateX(-100%)' }}
              className="fixed z-[9999] min-w-[190px] overflow-hidden rounded-md border border-slate-200 bg-panel shadow-lg dark:border-gray-700"
            >
              {OPTIONS.map((opt) => (
                <button
                  key={opt.tracetype}
                  type="button"
                  role="menuitem"
                  onClick={() => run(opt.tracetype, opt.label)}
                  className="block w-full px-3 py-2 text-left text-[0.8125rem] text-slate-700 hover:bg-slate-100 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>,
          document.body,
        )}

      <TraceResultModal
        open={results !== null}
        title={title}
        results={results ?? []}
        onClose={() => setResults(null)}
      />
    </div>
  );
};

export default TraceAllButton;
